import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSong } from "@/context/SongContext";
import axios from "axios";
import { Waveform, MusicNotes, SignOut, Clock, Lightning, Phone, CreditCard, X, HourglassMedium } from "@phosphor-icons/react";
import { toast } from "sonner";
import { trackEvent, Events } from "@/lib/analytics";
import { SongPlayerCard } from "@/components/SongPlayerCard";
import { API } from "@/lib/apiBase";

const COST = 30;
const MODELS = [
  { id: "V6", name: "V6", hint: "Best quality" },
  { id: "V6_WILD", name: "V6 Wild", hint: "Experimental" },
  { id: "V6_MINI", name: "V6 Mini", hint: "Fast" },
];
const SINGERS = [
  { v: "male", label: "Male" },
  { v: "female", label: "Female" },
  { v: "both", label: "Both (Duet)" },
];
const labelCls = "text-xs uppercase tracking-[0.15em] font-bold block mb-2 text-[#F59E0B]";
const boxCls = "w-full bg-[#0A0A0C] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-500 outline-none focus:border-[#F59E0B] transition-colors text-sm";

const PillGroup = ({ options, value, onChange, testPrefix }) => (
  <div className="grid grid-cols-3 gap-2">
    {options.map((o) => (
      <button key={o.v} type="button" onClick={() => onChange(o.v)} data-testid={`${testPrefix}-${o.v}`}
        className={`rounded-xl px-3 py-2.5 text-sm font-medium transition-colors border ${value === o.v ? "bg-[#F59E0B] text-black border-[#F59E0B]" : "bg-[#0A0A0C] text-white/80 border-white/10 hover:border-white/30"}`}>
        {o.label}
        {o.hint && <span className={`block text-[10px] font-normal ${value === o.v ? "text-black/70" : "text-white/40"}`}>{o.hint}</span>}
      </button>
    ))}
  </div>
);

const CreditsBanner = ({ user, credits, isAdmin }) => {
  if (isAdmin) return null;
  if (credits >= COST) {
    return (
      <div className="bg-[#141417] border border-[#F59E0B]/20 rounded-2xl p-5 mb-8 flex items-center justify-between gap-4" data-testid="credits-banner">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center"><Lightning size={24} weight="fill" style={{ color: "#F59E0B" }} /></div>
          <div>
            <p className="text-white font-medium" data-testid="credits-balance">{credits} credits</p>
            <p className="text-sm text-[#A1A1AA]">≈ {Math.floor(credits / COST)} generations ({Math.floor(credits / COST) * 2} songs){user.subscription?.expires_at && ` · Plan renews by ${new Date(user.subscription.expires_at).toLocaleDateString()}`}</p>
          </div>
        </div>
        <Link to="/pricing" className="text-sm text-[#F59E0B] hover:text-white transition-colors whitespace-nowrap" data-testid="buy-credits-link">Buy credits</Link>
      </div>
    );
  }
  if (user.free_credits_status !== "approved") {
    return (
      <div className="bg-[#141417] border border-[#F59E0B]/30 rounded-2xl p-6 mb-8 flex items-start gap-4" data-testid="approval-pending-banner">
        <HourglassMedium size={28} style={{ color: "#F59E0B" }} className="shrink-0" />
        <div className="flex-1">
          <p className="text-white font-medium mb-1">Account under review — 250 free credits coming</p>
          <p className="text-sm text-[#A1A1AA] mb-3">Once our team approves your account, 250 free credits will be added and you'll get an email. Need to start now? Subscribe below.</p>
          <Link to="/pricing" className="bg-[#F59E0B] text-black font-semibold rounded-full px-5 py-2 text-sm hover:bg-[#D97706] transition-colors inline-flex items-center gap-2" data-testid="pending-subscribe-btn"><CreditCard size={16} weight="bold" /> ₹899 / month · 2500 credits</Link>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-[#141417] border border-[#F59E0B]/30 rounded-2xl p-6 mb-8 text-center" data-testid="subscription-banner">
      <p className="text-white font-medium mb-1">You have {credits} credits left</p>
      <p className="text-sm text-[#A1A1AA] mb-3">Each generation (2 songs) needs {COST} credits.</p>
      <Link to="/pricing" className="bg-[#F59E0B] text-black font-semibold rounded-full px-6 py-2.5 text-sm hover:bg-[#D97706] transition-colors inline-flex items-center gap-2" data-testid="subscribe-cta-btn"><CreditCard size={16} weight="bold" /> Subscribe — ₹899 / month</Link>
    </div>
  );
};

const DareMusicHome = () => {
  const { user, logout, refreshUser } = useAuth();
  const { editedLyrics: lyrics, setEditedLyrics: setLyrics, editedPrompt: style, setEditedPrompt: setStyle, concept: songName, setConcept: setSongName, songData: generation, setSongData: setGeneration, clearAll } = useSong();
  const navigate = useNavigate();
  const [model, setModel] = useState("V6");
  const [singer, setSinger] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const credits = isAdmin ? Infinity : Number(user?.credits || 0);
  const isProcessing = generation?.songs?.some((s) => s.status === "processing");
  const userInitial = (user?.name || user?.email || "U").charAt(0).toUpperCase();

  useEffect(() => {
    const planName = sessionStorage.getItem("daremusics_just_paid");
    if (planName) {
      sessionStorage.removeItem("daremusics_just_paid");
      setTimeout(() => toast.success(`${planName} plan activated — 2500 credits added!`, { duration: 6000 }), 400);
    }
  }, []);

  useEffect(() => {
    if (!generation?.generation_id || !isProcessing) return;
    let stopped = false;
    const poll = async () => {
      while (!stopped) {
        await new Promise((r) => setTimeout(r, 6000));
        if (stopped) return;
        try {
          const res = await axios.get(`${API}/generation/${generation.generation_id}`, { withCredentials: true });
          if (res.data.status === "done") {
            setGeneration(res.data);
            try { await refreshUser(); } catch { /* non-fatal */ }
            const ok = res.data.songs.filter((s) => s.status === "completed").length;
            trackEvent(Events.SONG_GENERATED, { generation_id: res.data.generation_id, songs: ok });
            ok ? toast.success(`${ok} song${ok > 1 ? "s" : ""} ready!`) : toast.error("Generation failed. Credits refunded.");
            return;
          }
        } catch { /* keep polling */ }
      }
    };
    poll();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generation?.generation_id, isProcessing]);

  const handleGenerate = async () => {
    if (!user) { navigate("/login"); return; }
    if (!lyrics.trim()) { toast.error("Please enter your lyrics."); return; }
    if (!style.trim()) { toast.error("Please enter a music style."); return; }
    if (!singer) { toast.error("Please select a singer (Male / Female / Both)."); return; }
    if (credits < COST) { toast.error(`Not enough credits. Each generation uses ${COST} credits.`); navigate("/pricing"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/generate-song`, { lyrics, style, model, singer, song_name: songName }, { withCredentials: true });
      setGeneration(res.data);
      try { await refreshUser(); } catch { /* non-fatal */ }
      toast.success("Creating 2 songs. This takes 2-3 minutes…");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start song generation");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => { await logout(); clearAll(); navigate("/login"); };

  return (
    <div className="min-h-screen" style={{ background: "#0A0A0C" }}>
      <header className="relative z-10 backdrop-blur-xl bg-[#0A0A0C]/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Waveform size={28} weight="bold" style={{ color: "#F59E0B" }} />
            <h1 className="text-xl font-light tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Daremusics</h1>
          </div>
          <div className="flex items-center gap-4">
            {user ? (
              <>
                <span className="hidden sm:flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#F59E0B]/10 text-[#F59E0B]" data-testid="credits-badge">
                  <Lightning size={12} weight="fill" /> {isAdmin ? "Unlimited" : `${credits} credits`}
                </span>
                <Link to="/history" className="hidden sm:block text-white/60 hover:text-white text-sm transition-colors" data-testid="nav-history">Songs</Link>
                <Link to="/pricing" className="hidden sm:block text-white/60 hover:text-white text-sm transition-colors" data-testid="nav-pricing">Plans</Link>
                {isAdmin && <Link to="/admin" className="hidden sm:block text-white/60 hover:text-white text-sm transition-colors" data-testid="nav-admin">Admin</Link>}
                <span className="hidden sm:block text-white/60 text-sm">{user.name}</span>
                <button onClick={handleLogout} className="hidden sm:flex items-center gap-1 text-white/40 hover:text-white transition-colors text-sm" data-testid="logout-btn-desktop"><SignOut size={18} /> Logout</button>
                <button onClick={() => setProfileOpen(true)} className="sm:hidden w-9 h-9 rounded-full bg-black border-2 border-[#F59E0B] flex items-center justify-center text-[#F59E0B] font-bold text-sm" data-testid="mobile-profile-btn">{userInitial}</button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-white/60 hover:text-white text-sm transition-colors" data-testid="nav-login">Login</Link>
                <Link to="/register" className="bg-[#F59E0B] text-black font-semibold rounded-full px-5 py-2 text-sm hover:bg-[#D97706] transition-colors" data-testid="nav-register">Sign Up</Link>
              </>
            )}
          </div>
        </div>
      </header>

      {user && profileOpen && (
        <div className="fixed inset-0 z-50 sm:hidden" onClick={() => setProfileOpen(false)}>
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute right-0 top-0 h-full w-72 bg-[#0A0A0C] border-l border-[#F59E0B]/20 flex flex-col" data-testid="mobile-profile-menu" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/5 bg-[#141417] flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-white text-sm font-medium truncate">{user.name}</p>
                <p className="text-[#A1A1AA] text-xs truncate">{user.email}</p>
                <p className="text-[#F59E0B] text-sm font-bold mt-2" data-testid="mobile-credits">{isAdmin ? "Unlimited" : `${credits} credits`}</p>
              </div>
              <button onClick={() => setProfileOpen(false)} className="text-white/40 hover:text-white" data-testid="close-profile-menu"><X size={20} /></button>
            </div>
            <div className="flex-1 py-2">
              {[["/history", Clock, "Song History", "mobile-nav-history"], ["/subscription-history", CreditCard, "Subscription History", "mobile-nav-sub-history"], ["/pricing", CreditCard, "Plans", "mobile-nav-pricing"]].map(([to, Icon, label, tid]) => (
                <button key={to} onClick={() => { navigate(to); setProfileOpen(false); }} className="flex items-center gap-3 px-5 py-3.5 text-white/80 hover:text-white hover:bg-[#141417] transition-colors text-sm w-full text-left" data-testid={tid}><Icon size={18} style={{ color: "#F59E0B" }} /> {label}</button>
              ))}
              <a href="tel:9493926054" className="flex items-center gap-3 px-5 py-3.5 text-white/80 hover:text-white hover:bg-[#141417] transition-colors text-sm" data-testid="mobile-nav-support"><Phone size={18} style={{ color: "#F59E0B" }} /> Support</a>
            </div>
            <div className="border-t border-white/5 p-4">
              <button onClick={handleLogout} className="flex items-center justify-center gap-2 w-full bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors text-sm font-medium py-3 rounded-xl" data-testid="mobile-logout-btn"><SignOut size={18} /> Logout</button>
            </div>
          </div>
        </div>
      )}

      <main className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-12">
        <div className="mb-10 max-w-3xl">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Your lyrics. <span style={{ color: "#F59E0B" }}>Two studio songs.</span>
          </h2>
          <p className="text-base text-[#A1A1AA]">Paste your lyrics, describe the style, pick a model — get 2 full-length versions in minutes. {COST} credits per generation.</p>
          {!user && <Link to="/register" className="mt-6 bg-[#F59E0B] text-black font-semibold rounded-full px-8 py-3 hover:bg-[#D97706] transition-colors inline-block" data-testid="hero-signup-btn">Get Started</Link>}
        </div>

        {user && <CreditsBanner user={user} credits={credits} isAdmin={isAdmin} />}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8">
          <div className="lg:col-span-3 bg-[#141417] border border-white/5 rounded-2xl p-6 md:p-8 space-y-5" data-testid="generate-form">
            <div>
              <label className={labelCls}>Song Title</label>
              <input data-testid="song-name-input" type="text" maxLength={80} value={songName} onChange={(e) => setSongName(e.target.value)} placeholder="e.g. Nee Navve" className={boxCls} />
            </div>
            <div>
              <div className="flex items-center justify-between"><label className={labelCls}>Lyrics <span className="text-red-500">*</span></label><span className="text-[10px] text-white/40" data-testid="lyrics-count">{lyrics.length}/5000</span></div>
              <textarea data-testid="lyrics-input" value={lyrics} maxLength={5000} onChange={(e) => setLyrics(e.target.value)} rows={12} placeholder={"[Verse 1]\nWrite or paste your lyrics here...\n\n[Chorus]\n..."} className={`${boxCls} resize-none leading-relaxed custom-scrollbar`} />
            </div>
            <div>
              <label className={labelCls}>Music Style <span className="text-red-500">*</span></label>
              <textarea data-testid="style-input" value={style} maxLength={900} onChange={(e) => setStyle(e.target.value)} rows={3} placeholder="e.g. Telugu folk, dappu beats, energetic, 120 bpm" className={`${boxCls} resize-none`} />
            </div>
            <div>
              <label className={labelCls}>Model</label>
              <PillGroup options={MODELS.map((m) => ({ v: m.id, label: m.name, hint: m.hint }))} value={model} onChange={setModel} testPrefix="model" />
            </div>
            <div>
              <label className={labelCls}>Singer <span className="text-red-500">*</span></label>
              <PillGroup options={SINGERS} value={singer} onChange={setSinger} testPrefix="singer" />
            </div>
            <button data-testid="generate-song-btn" onClick={handleGenerate} disabled={submitting || isProcessing} className="bg-[#F59E0B] text-black font-semibold rounded-full px-8 py-3.5 hover:bg-[#D97706] hover:-translate-y-0.5 transition-[transform,background-color] duration-300 shadow-[0_0_20px_rgba(245,158,11,0.2)] disabled:opacity-50 disabled:cursor-not-allowed w-full flex items-center justify-center gap-2">
              {submitting || isProcessing ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <MusicNotes size={20} weight="fill" />}
              {isProcessing ? "Creating 2 songs (2-3 min)…" : `Generate 2 Songs · ${COST} credits`}
            </button>
          </div>

          <div className="lg:col-span-2 bg-[#141417] border border-white/5 rounded-2xl p-6 md:p-8" data-testid="results-panel">
            <div className="flex items-center justify-between mb-5">
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#F59E0B]">Your Songs</label>
              {generation && !isProcessing && <button onClick={() => setGeneration(null)} className="text-xs text-white/40 hover:text-white transition-colors" data-testid="clear-results-btn">Clear</button>}
            </div>
            {generation?.songs?.length ? (
              <div className="space-y-4">
                {generation.songs.map((s) => (
                  <div key={s.id} className="bg-[#1C1C21] border border-white/10 rounded-2xl p-4"><SongPlayerCard song={s} /></div>
                ))}
                <p className="text-xs text-[#A1A1AA]">Saved automatically to <Link to="/history" className="text-[#F59E0B] hover:underline">Song History</Link>.</p>
              </div>
            ) : (
              <div className="text-center py-12" data-testid="results-empty">
                <MusicNotes size={40} className="mx-auto mb-3 text-white/20" />
                <p className="text-sm text-[#A1A1AA]">Two versions of your song will appear here.</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-16 border-t border-white/5 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-6 flex-wrap">
            <Link to="/refund-policy" className="text-white/40 hover:text-white text-sm transition-colors" data-testid="footer-refund">Refund Policy</Link>
            <Link to="/privacy-policy" className="text-white/40 hover:text-white text-sm transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" className="text-white/40 hover:text-white text-sm transition-colors" data-testid="footer-terms">Terms & Conditions</Link>
            <Link to="/pricing" className="text-white/40 hover:text-white text-sm transition-colors" data-testid="footer-pricing">Pricing</Link>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Phone size={14} style={{ color: "#F59E0B" }} />
            <span className="text-[#A1A1AA]">Support:</span>
            <a href="tel:9493926054" className="text-white/60 hover:text-[#F59E0B] transition-colors">9493926054</a>
            <span className="text-white/20">|</span>
            <a href="tel:9493924588" className="text-white/60 hover:text-[#F59E0B] transition-colors">9493924588</a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DareMusicHome;
